import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { calculateIkrProgress } from '../../lib/utils';
import KPIProgressBar from '../../components/ui/KPIProgressBar';
import OwnerIKRThreadFY2627 from './OwnerIKRThreadFY2627';

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

function isThreadIkr(ikr) { return Array.isArray(ikr?.quarters); }

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

function buildLegacyQuarter(ikr, quarterIndex) {
  const actuals = Array.isArray(ikr.quarterlyActuals) ? ikr.quarterlyActuals : [];
  return { q: QUARTERS[quarterIndex], text: quarterIndex === 0 ? (ikr.text || '') : '', target: null, actual: toNumberOrNull(actuals[quarterIndex]) };
}

function toThreadIkr(ikr) {
  if (isThreadIkr(ikr)) {
    const quarters = QUARTERS.map((code, i) => {
      const q = ikr.quarters?.[i] || {};
      return { q: code, text: q.text || '', target: q.target ?? null, actual: q.actual ?? null };
    });
    return {
      ...ikr,
      ownerId: ikr.ownerId || null,
      yearlyText: ikr.yearlyText || quarters.find(q => q.text)?.text || '',
      yearlyTarget: ikr.yearlyTarget ?? null,
      kpiUnit: ikr.kpiUnit || '',
      calculationMethod: ikr.calculationMethod || 'sum',
      quarters,
    };
  }
  return {
    id: ikr.id || `ikr_${Date.now()}`,
    ownerId: ikr.ownerId || null,
    yearlyText: ikr.text || '',
    yearlyTarget: ikr.kpiTarget ?? null,
    kpiUnit: ikr.kpiUnit || '',
    calculationMethod: ikr.calculationMethod || 'sum',
    quarters: QUARTERS.map((_, i) => buildLegacyQuarter(ikr, i)),
    selfNotes: Array.isArray(ikr.selfNotes) ? ikr.selfNotes : ['', '', '', ''],
    managerNotes: Array.isArray(ikr.managerNotes) ? ikr.managerNotes : ['', '', '', ''],
  };
}

function normalizeThreads(list) { return (list || []).map(toThreadIkr); }

function getStatusInfo(progress) {
  if (progress >= 1.0) return { label: 'Ahead',    badge: 'bg-emerald-100 text-emerald-700' };
  if (progress >= 0.7) return { label: 'On Track', badge: 'bg-green-100 text-green-700'     };
  if (progress >= 0.3) return { label: 'At Risk',  badge: 'bg-amber-100 text-amber-700'     };
  return               { label: 'Behind',  badge: 'bg-red-100 text-red-700'         };
}

export default function TeamKRRow({ tkr, onUpdate, onDelete, users, viewer, krCode, onOwnerAssigned }) {
  const [tkrState, setTkrState] = useState({ ...tkr, individualKRs: normalizeThreads(tkr.individualKRs) });
  /** Start expanded so opening the parent KR reveals owners / IKRs without an extra click */
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(tkr.text === 'New Team Key Result');
  const [tkrText, setTkrText] = useState(tkr.text);
  const canEdit = viewer.role === 'Admin';

  useEffect(() => {
    setTkrState({ ...tkr, individualKRs: normalizeThreads(tkr.individualKRs) });
    setTkrText(tkr.text);
  }, [tkr]);

  const persistTkr = (nextState) => { setTkrState(nextState); onUpdate(nextState); };

  const handleIKRUpdate = (ikrId, updatedIkr) => {
    const newIkrs = tkrState.individualKRs.map(ikr => (ikr.id === ikrId ? updatedIkr : ikr));
    persistTkr({ ...tkrState, individualKRs: newIkrs });
  };

  const addIndividualKr = () => {
    const newIkr = {
      id: `ikr_${Date.now()}`,
      ownerId: null,
      yearlyText: '',
      yearlyTarget: null,
      kpiUnit: '',
      calculationMethod: 'sum',
      quarters: QUARTERS.map(q => ({ q, text: '', target: null, actual: null })),
      selfNotes: ['', '', '', ''],
      managerNotes: ['', '', '', ''],
    };
    persistTkr({ ...tkrState, individualKRs: [...(tkrState.individualKRs || []), newIkr] });
    setIsExpanded(true);
  };

  const deleteIndividualKr = (ikrId) => {
    persistTkr({ ...tkrState, individualKRs: tkrState.individualKRs.filter(ikr => ikr.id !== ikrId) });
  };

  const handleUpdate = (field, value) => persistTkr({ ...tkrState, [field]: value });

  const saveTkrText = () => { setIsEditing(false); handleUpdate('text', tkrText); };

  const individualKRs = tkrState.individualKRs || [];
  const overallProgress = individualKRs.length > 0
    ? individualKRs.reduce((sum, ikr) => sum + calculateIkrProgress(ikr), 0) / individualKRs.length
    : 0;
  const status = getStatusInfo(overallProgress);
  const ownerCount = individualKRs.filter(ikr => ikr.ownerId).length;

  return (
    <div className="rounded-lg bg-white border border-gray-200 mb-2 overflow-hidden">

      {/* ── TKR Header Row ─────────────────────────────────────── */}
      <div className="flex items-start gap-3 p-3 hover:bg-gray-50/60 transition-colors">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-0.5 flex-shrink-0 text-gray-400 hover:text-blue-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
        >
          {isExpanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>

        {/* TKR text + target meta */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <input
              type="text"
              value={tkrText}
              onChange={(e) => setTkrText(e.target.value)}
              onBlur={saveTkrText}
              onKeyPress={(e) => { if (e.key === 'Enter') saveTkrText(); }}
              className="w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
              disabled={!canEdit}
            />
          ) : (
            <p
              className={`text-sm font-semibold text-gray-700 ${canEdit ? 'cursor-pointer hover:text-blue-600' : ''}`}
              onClick={() => canEdit && setIsEditing(true)}
            >
              {tkrState.text}
            </p>
          )}

          {/* Target / Unit — editable for admin, pills for non-admin */}
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {canEdit ? (
              <>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span>Target KPI</span>
                  <input
                    type="text"
                    value={tkrState.targetKPI || ''}
                    placeholder="e.g. 95"
                    onChange={(e) => handleUpdate('targetKPI', e.target.value)}
                    className="w-20 px-2 py-1 border border-gray-200 rounded-md text-xs focus:ring-1 focus:ring-blue-500 bg-white ml-0.5"
                  />
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span>Unit</span>
                  <input
                    type="text"
                    value={tkrState.unit || ''}
                    placeholder="e.g. %"
                    onChange={(e) => handleUpdate('unit', e.target.value)}
                    className="w-16 px-2 py-1 border border-gray-200 rounded-md text-xs focus:ring-1 focus:ring-blue-500 bg-white ml-0.5"
                  />
                </div>
              </>
            ) : (
              (tkrState.targetKPI || tkrState.unit) && (
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md">
                  Target: <span className="font-medium text-gray-700">{tkrState.targetKPI || '—'} {tkrState.unit || ''}</span>
                </span>
              )
            )}
            {ownerCount > 0 && (
              <span className="text-xs text-gray-400">
                {ownerCount} owner{ownerCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Progress + status + delete */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full hidden sm:inline-flex ${status.badge}`}>
            {status.label}
          </span>
          <div className="flex items-center gap-1.5 w-28">
            <div className="flex-1"><KPIProgressBar value={overallProgress} /></div>
            <span className="text-xs font-bold w-9 text-right text-gray-700 tabular-nums">
              {(overallProgress * 100).toFixed(0)}%
            </span>
          </div>
          {canEdit && (
            <button
              onClick={() => onDelete(tkr.id)}
              className="text-gray-300 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors flex-shrink-0"
              title="Delete team KR"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ── Expanded: Owner IKR Cards ──────────────────────────── */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-2 border-t border-gray-100 bg-gray-50/30">
          {individualKRs.length === 0 && (
            <p className="text-xs text-gray-400 italic py-2 pl-1">No owner threads yet.</p>
          )}
          {individualKRs.map(ikr => (
            <OwnerIKRThreadFY2627
              key={ikr.id}
              ikr={ikr}
              onUpdate={handleIKRUpdate}
              onDelete={deleteIndividualKr}
              users={users}
              viewer={viewer}
              krCode={krCode}
              onOwnerAssigned={onOwnerAssigned}
            />
          ))}
          {canEdit && (
            <button
              type="button"
              onClick={addIndividualKr}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold mt-1 pl-1"
            >
              <Plus size={13} /> Add Owner Thread
            </button>
          )}
        </div>
      )}
    </div>
  );
}
